import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreVertical,
  Edit,
  LogOut,
  ArrowLeft,
  MapPin,
  Phone,
  Calendar,
  FileText,
  Pill,
} from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { format, isValid, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface PatientHeaderProps {
  patient: {
    id: string
    full_name: string
    status: 'active' | 'inactive' | 'discharged' | 'deleted'
    condition_description?: string | null
    birth_date?: string | null
    address?: string | null
    emergency_contact?: string | null
    medical_history?: string | null
    current_medications?: string[] | null
    created_at?: string
  }
  onEdit?: () => void
  onDelete?: () => void
}

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-yellow-100 text-yellow-800',
  discharged: 'bg-gray-100 text-gray-800',
  deleted: 'bg-red-100 text-red-800',
}

const statusLabels: Record<string, string> = {
  active: 'Ativo',
  inactive: 'Inativo',
  discharged: 'Alta',
  deleted: 'Excluído',
}

function getAge(birthDate: string | null | undefined): string | null {
  if (!birthDate) return null
  const birth = new Date(birthDate)
  if (!isValid(birth)) return null
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return `${age} anos`
}

function formatBirthDate(birthDate: string | null | undefined): string | null {
  if (!birthDate) return null
  const date = typeof birthDate === 'string' ? parseISO(birthDate) : new Date(birthDate)
  if (!isValid(date)) return null
  return format(date, "d 'de' MMMM 'de' yyyy", { locale: ptBR })
}

export function PatientHeader({ patient, onEdit, onDelete }: PatientHeaderProps) {
  const age = getAge(patient.birth_date)
  const birthDateFormatted = formatBirthDate(patient.birth_date)
  const hasMedications = patient.current_medications && patient.current_medications.length > 0

  return (
    <div className="pb-6 border-b">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Link
            to="/patients"
            className="mt-1 p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-muted-foreground" />
          </Link>

          <div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h1 className="text-xl sm:text-2xl font-bold">{patient.full_name}</h1>
              <Badge className={cn('text-sm', statusColors[patient.status])}>
                {statusLabels[patient.status]}
              </Badge>
            </div>

            {/* Primary Info Line */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-muted-foreground">
              {age && <span>{age}</span>}
              {patient.condition_description && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <span>{patient.condition_description}</span>
                </>
              )}
            </div>

            {/* Additional Info */}
            <div className="mt-3 flex flex-wrap gap-x-3 sm:gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {birthDateFormatted && (
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  <span>Nascimento: {birthDateFormatted}</span>
                </div>
              )}
              {patient.address && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" />
                  <span>{patient.address}</span>
                </div>
              )}
              {patient.emergency_contact && (
                <div className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4" />
                  <span>Emergência: {patient.emergency_contact}</span>
                </div>
              )}
            </div>

            {/* Medications */}
            {hasMedications && (
              <div className="mt-3 flex items-start gap-1.5 text-sm">
                <Pill className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex flex-wrap gap-1.5">
                  {patient.current_medications!.map((med, i) => (
                    <Badge key={i} variant="outline" className="text-xs font-normal">
                      {med}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Medical History Preview */}
            {patient.medical_history && (
              <div className="mt-3 flex items-start gap-1.5 text-sm">
                <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                <p className="text-muted-foreground line-clamp-2 max-w-2xl">
                  {patient.medical_history}
                </p>
              </div>
            )}
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onEdit && (
              <DropdownMenuItem onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Editar
              </DropdownMenuItem>
            )}
            {onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onDelete}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Dar Alta
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
